'use client'

import React, { HTMLAttributes, useContext } from 'react'
import { TableContext } from '../../../../providers'
import { Column } from '../../../../types/Table'
import { useStickyColumn } from '../hooks/UseStickyColumn'
import { TagColors, colorBackgroundClasses } from '../../../atoms'

export const TABLE_CELL_ROOT_CLASS = 'table-cell'

export interface TableCellProps extends Omit<HTMLAttributes<HTMLTableCellElement>, 'color'> {
    children?: React.ReactNode
    colSpan?: number
    column?: Column
    hasNoPadding?: boolean
    isEditable?: boolean
    stretchHeight?: boolean
    ignoreMinHeight?: boolean
    color?: TagColors
}

export const TableCell = ({
    children,
    colSpan,
    column,
    hasNoPadding,
    isEditable,
    stretchHeight = false,
    ignoreMinHeight,
    color,
    ...props
}: TableCellProps) => {
    const { isEditable: tableIsEditable, isNested, size, hasBackground } = useContext(TableContext)
    const { ref, styling, getStickyClasses } = useStickyColumn(column)

    const getClasses = (): string => {
        let classes = TABLE_CELL_ROOT_CLASS
        switch (column?.alignment) {
            case 'start':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--start-aligned`
                break
            case 'center':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--center-aligned`
                break
            case 'end':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--end-aligned`
                break
        }
        switch (size) {
            case 'small':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--small`
                break
            case 'medium':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--medium`
                break
            case 'large':
                classes += ` ${TABLE_CELL_ROOT_CLASS}--large`
                break
        }
        if (column?.isActionColumn) classes += ` ${TABLE_CELL_ROOT_CLASS}--is-actions-cell`
        if (column?.hasNoPadding || hasNoPadding) classes += ` ${TABLE_CELL_ROOT_CLASS}--has-no-padding`
        if (!column?.hasNoPadding && !hasNoPadding && !isEditable && !stretchHeight && !column?.isActionColumn)
            classes += ` ${TABLE_CELL_ROOT_CLASS}--has-regular-padding`
        if (isEditable) classes += ` ${TABLE_CELL_ROOT_CLASS}--is-editable`
        if (stretchHeight) classes += ` ${TABLE_CELL_ROOT_CLASS}--stretch-height`
        if (column?.lineWrapping && column.lineWrapping >= 1) classes += ` ${TABLE_CELL_ROOT_CLASS}--wrapped`
        if (column?.lineWrapping && column.lineWrapping === 2) classes += ` ${TABLE_CELL_ROOT_CLASS}--wrapped-two-lines`
        if (column?.isStretched) classes += ` ${TABLE_CELL_ROOT_CLASS}--stretched`
        classes += ` ${getStickyClasses(TABLE_CELL_ROOT_CLASS)}`
        if (isNested) classes += ` ${TABLE_CELL_ROOT_CLASS}--nested`
        if (ignoreMinHeight) classes += ` ${TABLE_CELL_ROOT_CLASS}--ignore-min-height`
        if (tableIsEditable) classes += ` ${TABLE_CELL_ROOT_CLASS}--table-is-editable`
        if (!hasBackground) classes += ` ${TABLE_CELL_ROOT_CLASS}--no-background`
        if (color !== null && color !== undefined) classes += ` ${colorBackgroundClasses[color]}`
        if (props.className) classes += ` ${props.className}`

        return classes
    }

    return (
        <td ref={ref} {...props} className={getClasses()} colSpan={colSpan} style={styling}>
            <div className={`${TABLE_CELL_ROOT_CLASS}__content`}>
                <div className={`${TABLE_CELL_ROOT_CLASS}__content-wrapping`}>{children}</div>
            </div>
        </td>
    )
}
